import { sanitizeReferencePayload } from './referenceDefaults';

export const SYNC_KEYS = [
  'photographers', 'retouchers', 'locations', 'products', 'consumables', 'consumablesItems',
  'inventoryData', 'inventorySupplies', 'inventoryInks', 'inventoryConsumables', 'inventorySchedule',
  'photographersPercentages', 'retouchersPercentages', 'additionalPhotographers', 'additionalRetouchers',
  'shiftOpen', 'shiftStatus', 'shiftOpenTime', 'selectedPhotographer', 'selectedSecondPhotographer',
  'selectedRetoucher', 'selectedLocation', 'photographersInShift', 'retouchersInShift',
  'salesData', 'defects', 'inkRequests', 'additionalPhotographersValues', 'additionalRetouchersValues',
  'lastShiftSummary', 'darkMode', 'inventoryAuditEmployees', 'inventoryAuditHistory', 'inventoryAuditLast',
  'lastExportedFileName'
];

export const REFERENCE_KEYS = [
  'photographers', 'retouchers', 'locations', 'products', 'consumablesItems',
  'inventoryData', 'inventorySupplies', 'inventoryInks', 'inventoryConsumables', 'inventorySchedule',
  'photographersPercentages', 'retouchersPercentages', 'additionalPhotographers', 'additionalRetouchers',
  'darkMode', 'inventoryAuditEmployees', 'inventoryAuditHistory', 'inventoryAuditLast',
];

export const SHIFT_PERSIST_KEYS = [
  'shiftOpen', 'shiftStatus', 'shiftOpenTime', 'selectedPhotographer', 'selectedSecondPhotographer',
  'selectedRetoucher', 'selectedLocation', 'photographersInShift', 'retouchersInShift',
  'salesData', 'defects', 'consumables', 'inkRequests', 'additionalPhotographersValues',
  'additionalRetouchersValues', 'lastShiftSummary', 'lastExportedFileName'
];

const CLOSED_SHIFT = {
  shiftStatus: 'Закрыта',
  shiftOpen: 'false',
};

let activePointId = null;
let patched = false;
let storageBatchDepth = 0;
let bootstrapShiftState = null;
let origGetItem;
let origSetItem;
let origRemoveItem;
const changeListeners = new Set();

export function getPointKey(key) {
  return activePointId ? `point:${activePointId}:${key}` : key;
}

function notifyChange(key) {
  if (storageBatchDepth > 0) return;
  changeListeners.forEach((fn) => {
    try {
      fn(key);
    } catch (e) {
      console.warn(e);
    }
  });
}

export function runStorageBatch(fn) {
  storageBatchDepth += 1;
  try {
    fn();
  } finally {
    storageBatchDepth -= 1;
  }
}

export function onSyncedKeyChange(listener) {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

function clearLegacyUnprefixedKeys() {
  if (!origRemoveItem) return;
  SYNC_KEYS.forEach((key) => {
    origRemoveItem(key);
  });
}

function writeRawKey(storageKey, value) {
  const str = typeof value === 'string' ? value : JSON.stringify(value);
  origSetItem(storageKey, str);
}

function serializeValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  return JSON.stringify(value);
}

function isEmptyReferenceValue(key, value) {
  if (!REFERENCE_KEYS.includes(key)) return false;
  const str = typeof value === 'string' ? value : JSON.stringify(value);
  return str === '[]' || str === '{}' || str === '""';
}

function isShiftOpenState(state = {}) {
  return state.shiftStatus === 'Открыта' || state.shiftOpen === 'true';
}

export async function loadPointState(pointId) {
  const res = await fetch(`/api/points/${pointId}/state`, { cache: 'no-store' });
  if (!res.ok) {
    const err = new Error(`Не удалось загрузить состояние точки (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

function readJsonDirect(key, fallback) {
  if (!activePointId) return fallback;
  try {
    const raw = origGetItem(getPointKey(key));
    if (raw === null || raw === undefined) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function normalizeSalesTimes(sales) {
  if (!Array.isArray(sales)) return [];
  return sales.map((sale) => {
    if (!sale || !sale.time) return sale;
    if (sale.time instanceof Date) return sale;
    const parsed = new Date(sale.time);
    return Number.isNaN(parsed.getTime()) ? sale : { ...sale, time: parsed };
  });
}

function coerceShiftDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function readShiftStateDirect() {
  ensureStoragePatch();
  if (!activePointId) {
    return {
      shiftStatus: 'Закрыта',
      shiftOpenTime: null,
      selectedPhotographer: null,
      selectedSecondPhotographer: null,
      selectedRetoucher: null,
      selectedLocation: null,
      photographersInShift: [],
      retouchersInShift: [],
      salesData: [],
      defects: { frames: 0, packages: 0, paper: 0, envelopes: 0 },
      consumables: {},
      inkRequests: {},
      additionalPhotographersValues: Array(29).fill(null),
      additionalRetouchersValues: Array(9).fill(null),
      lastShiftSummary: null,
      shiftOpen: false,
    };
  }

  const shiftOpenRaw = origGetItem(getPointKey('shiftOpen'));
  const shiftStatusRaw = origGetItem(getPointKey('shiftStatus'));
  const isOpen = shiftStatusRaw === 'Открыта' || shiftOpenRaw === 'true';
  const shiftOpenTimeRaw = origGetItem(getPointKey('shiftOpenTime'));

  return {
    shiftStatus: isOpen ? 'Открыта' : (shiftStatusRaw || 'Закрыта'),
    shiftOpenTime: coerceShiftDate(shiftOpenTimeRaw),
    selectedPhotographer: origGetItem(getPointKey('selectedPhotographer')) || null,
    selectedSecondPhotographer: origGetItem(getPointKey('selectedSecondPhotographer')) || null,
    selectedRetoucher: origGetItem(getPointKey('selectedRetoucher')) || null,
    selectedLocation: origGetItem(getPointKey('selectedLocation')) || null,
    photographersInShift: readJsonDirect('photographersInShift', []),
    retouchersInShift: readJsonDirect('retouchersInShift', []),
    salesData: normalizeSalesTimes(readJsonDirect('salesData', [])),
    defects: readJsonDirect('defects', { frames: 0, packages: 0, paper: 0, envelopes: 0 }),
    consumables: readJsonDirect('consumables', {}),
    inkRequests: readJsonDirect('inkRequests', {}),
    additionalPhotographersValues: readJsonDirect('additionalPhotographersValues', Array(29).fill(null)),
    additionalRetouchersValues: readJsonDirect('additionalRetouchersValues', Array(9).fill(null)),
    lastShiftSummary: readJsonDirect('lastShiftSummary', null),
    shiftOpen: isOpen,
  };
}

export function getBootstrapShiftState() {
  return bootstrapShiftState || readShiftStateDirect();
}

function normalizeLoadedShiftState() {
  const shift = readShiftStateDirect();
  if (!shift.shiftOpen && shift.shiftStatus !== 'Открыта') return;

  writeShiftKeysBulk({
    shiftStatus: 'Открыта',
    shiftOpen: 'true',
    shiftOpenTime: shift.shiftOpenTime,
    selectedPhotographer: shift.selectedPhotographer,
    selectedSecondPhotographer: shift.selectedSecondPhotographer,
    selectedRetoucher: shift.selectedRetoucher,
    selectedLocation: shift.selectedLocation,
    photographersInShift: shift.photographersInShift,
    retouchersInShift: shift.retouchersInShift,
    salesData: shift.salesData,
    defects: shift.defects,
    consumables: shift.consumables,
    inkRequests: shift.inkRequests,
    additionalPhotographersValues: shift.additionalPhotographersValues,
    additionalRetouchersValues: shift.additionalRetouchersValues,
    lastShiftSummary: shift.lastShiftSummary,
  }, { silent: true });
}

function ensureStoragePatch() {
  if (patched) return;

  origGetItem = localStorage.getItem.bind(localStorage);
  origSetItem = localStorage.setItem.bind(localStorage);
  origRemoveItem = localStorage.removeItem.bind(localStorage);

  clearLegacyUnprefixedKeys();

  localStorage.getItem = (key) => {
    if (activePointId && SYNC_KEYS.includes(key)) {
      return origGetItem(getPointKey(key));
    }
    return origGetItem(key);
  };

  localStorage.setItem = (key, value) => {
    if (activePointId && SYNC_KEYS.includes(key)) {
      origSetItem(getPointKey(key), value);
      notifyChange(key);
      return;
    }
    origSetItem(key, value);
  };

  localStorage.removeItem = (key) => {
    if (activePointId && SYNC_KEYS.includes(key)) {
      origRemoveItem(getPointKey(key));
      notifyChange(key);
      return;
    }
    origRemoveItem(key);
  };

  patched = true;
}

function sanitizeServerState(pointId, serverState = {}) {
  const clean = { ...serverState };
  const storedPointId = clean._pointId;
  delete clean._pointId;

  if (storedPointId && storedPointId !== pointId) {
    SHIFT_PERSIST_KEYS.forEach((key) => {
      delete clean[key];
    });
  }

  return clean;
}

export function applyPointStorage(pointId, serverState = {}) {
  ensureStoragePatch();
  activePointId = pointId;

  // Полная гидратация с сервера: очищаем все ключи точки, затем записываем с сервера.
  SYNC_KEYS.forEach((key) => {
    origRemoveItem(getPointKey(key));
  });

  const cleanState = sanitizeReferencePayload(sanitizeServerState(pointId, serverState));

  Object.entries(cleanState).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (isEmptyReferenceValue(key, value)) return;
    writeRawKey(getPointKey(key), value);
  });

  const hasShiftOnServer = isShiftOpenState(cleanState);
  const hasClosedOnServer =
    cleanState.shiftStatus === 'Закрыта' || cleanState.shiftOpen === 'false';
  const isNewPoint = Object.keys(cleanState).length === 0;

  if (!hasShiftOnServer && (hasClosedOnServer || isNewPoint)) {
    Object.entries(CLOSED_SHIFT).forEach(([key, value]) => {
      if (cleanState[key] === null || cleanState[key] === undefined) {
        writeRawKey(getPointKey(key), value);
      }
    });
  }

  normalizeLoadedShiftState();
  bootstrapShiftState = readShiftStateDirect();
}

export function writeFullSnapshot(data = {}) {
  ensureStoragePatch();
  runStorageBatch(() => {
    Object.entries(data).forEach(([key, value]) => {
      if (!SYNC_KEYS.includes(key)) return;
      if (value === undefined) return;
      if (isEmptyReferenceValue(key, value)) return;
      const str = serializeValue(value);
      if (str !== null) origSetItem(getPointKey(key), str);
    });
  });
}

export function writeShiftKeysBulk(data = {}, { silent = false } = {}) {
  ensureStoragePatch();
  if (!activePointId) return;

  runStorageBatch(() => {
    Object.entries(data).forEach(([key, value]) => {
      if (!SHIFT_PERSIST_KEYS.includes(key)) return;
      if (value === undefined) return;
      if (value === null) {
        origRemoveItem(getPointKey(key));
        return;
      }
      const str = serializeValue(value);
      if (str === null) return;
      origSetItem(getPointKey(key), str);
    });
  });

  if (!silent) {
    notifyChange('shiftStatus');
  }
}

export function getActivePointId() {
  return activePointId;
}

export function collectReferenceState(keys = REFERENCE_KEYS) {
  const state = {};
  if (!activePointId) return state;

  const keyList = Array.isArray(keys) ? keys : [keys];
  keyList.forEach((key) => {
    if (!REFERENCE_KEYS.includes(key)) return;
    const val = origGetItem(getPointKey(key));
    if (val !== null && !isEmptyReferenceValue(key, val)) {
      state[key] = val;
    }
  });

  state._pointId = activePointId;
  return state;
}

export function collectPointState() {
  return collectReferenceState(REFERENCE_KEYS);
}

export function shiftSnapshotToPayload(snapshot = {}) {
  const payload = {};

  if (snapshot.shiftStatus !== undefined) {
    payload.shiftStatus = snapshot.shiftStatus;
    payload.shiftOpen = snapshot.shiftStatus === 'Открыта' ? 'true' : 'false';
  }

  const assign = (key, value) => {
    if (value === undefined) return;
    if (value === null) {
      payload[key] = null;
      return;
    }
    payload[key] = value;
  };

  assign('shiftOpenTime', snapshot.shiftOpenTime instanceof Date ? snapshot.shiftOpenTime.toISOString() : snapshot.shiftOpenTime);
  assign('selectedPhotographer', snapshot.selectedPhotographer);
  assign('selectedSecondPhotographer', snapshot.selectedSecondPhotographer);
  assign('selectedRetoucher', snapshot.selectedRetoucher);
  assign('selectedLocation', snapshot.selectedLocation);
  assign('photographersInShift', snapshot.photographersInShift);
  assign('retouchersInShift', snapshot.retouchersInShift);
  assign('salesData', snapshot.salesData);
  assign('defects', snapshot.defects);
  assign('consumables', snapshot.consumables);
  assign('inkRequests', snapshot.inkRequests);
  assign('additionalPhotographersValues', snapshot.additionalPhotographersValues);
  assign('additionalRetouchersValues', snapshot.additionalRetouchersValues);
  assign('lastShiftSummary', snapshot.lastShiftSummary);

  return payload;
}

export function prepareShiftPayloadForServer(payload = {}) {
  const state = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined) return;
    // null = явная очистка поля (закрытие смены)
    if (value === null) {
      state[key] = null;
      return;
    }
    if (typeof value === 'string') {
      state[key] = value;
    } else {
      state[key] = JSON.stringify(value);
    }
  });

  const isExplicitlyClosed =
    state.shiftStatus === 'Закрыта' || state.shiftOpen === 'false';
  const isOpen =
    !isExplicitlyClosed &&
    (state.shiftStatus === 'Открыта' || state.shiftOpen === 'true');

  if (isOpen) {
    state.shiftStatus = 'Открыта';
    state.shiftOpen = 'true';
  } else if (state.shiftStatus || state.shiftOpen) {
    state.shiftStatus = 'Закрыта';
    state.shiftOpen = 'false';
  }

  return state;
}

export function collectShiftState() {
  const state = {};
  if (!activePointId) return state;

  SHIFT_PERSIST_KEYS.forEach((key) => {
    const val = origGetItem(getPointKey(key));
    if (val === null || val === undefined) return;
    if (isEmptyReferenceValue(key, val)) return;
    state[key] = val;
  });

  const isExplicitlyClosed =
    state.shiftStatus === 'Закрыта' || state.shiftOpen === 'false';
  const isOpen =
    !isExplicitlyClosed &&
    (state.shiftStatus === 'Открыта' || state.shiftOpen === 'true');

  if (isOpen) {
    state.shiftStatus = 'Открыта';
    state.shiftOpen = 'true';
  } else if (state.shiftStatus || state.shiftOpen) {
    state.shiftStatus = 'Закрыта';
    state.shiftOpen = 'false';
  }

  return state;
}

export function readStoredJson(key, fallback) {
  ensureStoragePatch();
  if (!activePointId) return fallback;
  try {
    const raw = origGetItem(getPointKey(key));
    if (raw === null || raw === undefined) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

/** Запись справочника без отложенной автосинхронизации (синхронизация — явно из UI). */
export function writeReferenceJson(key, value) {
  ensureStoragePatch();
  if (!activePointId || !REFERENCE_KEYS.includes(key)) return;
  const str = typeof value === 'string' ? value : JSON.stringify(value);
  runStorageBatch(() => {
    writeRawKey(getPointKey(key), str);
  });
}

/** Подтянуть справочники с сервера в localStorage (без сброса данных смены). */
export function applyReferenceFromServer(serverState = {}) {
  ensureStoragePatch();
  if (!activePointId) return;
  runStorageBatch(() => {
    REFERENCE_KEYS.forEach((key) => {
      const value = serverState[key];
      if (value === null || value === undefined) return;
      if (isEmptyReferenceValue(key, value)) return;
      writeRawKey(getPointKey(key), value);
    });
  });
}

export async function refreshReferenceFromServer() {
  if (!activePointId) return null;
  const state = await loadPointState(activePointId);
  applyReferenceFromServer(state);
  return state;
}
